import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ResponsiveImage } from "@/shared/components/ui/responsive-image";
import { BRANDING } from "@/shared/config/branding";
import { cn } from "@/shared/lib/utils";
import { FeedbackButton } from "./FeedbackButton";

interface AppShellProps {
  center?: ReactNode;
  right?: ReactNode;
  banner?: ReactNode;
  /**
   * Optional secondary nav row, rendered below the main header. Used for
   * centered view-switcher tabs (Editor / Preview / Analytics) inside a
   * single proposal so views (links) are visually separated from actions
   * (buttons in `right`).
   */
  subnav?: ReactNode;
  fill?: boolean;
  /**
   * Override where the back arrow goes. When provided, the arrow calls this
   * instead of navigating to /dashboard (e.g. exiting an in-page preview mode).
   */
  onBack?: () => void;
  children: ReactNode;
}

export function AppShell({
  center,
  right,
  banner,
  subnav,
  fill = false,
  onBack,
  children,
}: AppShellProps) {
  const { pathname } = useLocation();
  const isDashboard = pathname === "/dashboard";

  return (
    <div
      className={
        fill ? "flex h-screen flex-col bg-page" : "min-h-screen bg-page"
      }
    >
      {banner}
      <header className="flex flex-shrink-0 items-center gap-4 border-b border-gray-200 bg-white px-4 py-3 sm:px-8">
        {/* Left group: back affordance + title slot. flex-1 so the title fills the
            available space when there's no inline subnav. */}
        <div className="flex min-w-0 flex-1 items-center gap-4">
          {isDashboard ? (
            <Link
              to="/dashboard"
              className="flex-shrink-0"
              title="Dashboard"
              aria-label="Dashboard"
            >
              <ResponsiveImage
                src="/proposalkit_logo.png"
                alt={BRANDING.name}
                width={444}
                height={120}
                className="h-8 w-auto"
                loading="eager"
              />
            </Link>
          ) : onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="-ml-2 inline-flex h-9 flex-shrink-0 items-center justify-center gap-2 rounded-lg px-2 text-sm font-medium text-slate-500 transition hover:bg-surface-hover hover:text-slate-900 md:-ml-3 md:px-3"
              title="Back"
              aria-label="Back"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={2} />
              <span className="hidden md:inline">Back</span>
            </button>
          ) : (
            <Link
              to="/dashboard"
              className="-ml-2 inline-flex h-9 flex-shrink-0 items-center justify-center gap-2 rounded-lg px-2 text-sm font-medium text-slate-500 transition hover:bg-surface-hover hover:text-slate-900 md:-ml-3 md:px-3"
              title="Back to dashboard"
              aria-label="Back to dashboard"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={2} />
              <span className="hidden md:inline">Back to dashboard</span>
            </Link>
          )}
          <div className="min-w-0 flex-1">{center}</div>
        </div>
        {/* Center: subnav (e.g. ProposalViewTabs). Renders here on lg+; below
            lg it falls to a second row. */}
        {subnav && (
          <div className="hidden flex-shrink-0 items-center justify-center lg:flex">
            {subnav}
          </div>
        )}
        {/* Right group. flex-1 (matching the left) only when an inline subnav is
            shown — otherwise we'd waste space and miscentered the page header. */}
        {(right || !isDashboard) && (
          <div
            className={cn(
              "flex flex-shrink-0 items-center justify-end gap-3",
              subnav && "lg:flex-1",
            )}
          >
            {right}
            {!isDashboard && (
              <Link
                to="/dashboard"
                className="flex-shrink-0"
                title="Dashboard"
                aria-label="Dashboard"
              >
                <img
                  src="/apple-touch-icon.png"
                  alt={BRANDING.name}
                  width={32}
                  height={32}
                  className="h-8 w-8 rounded-md"
                  loading="eager"
                />
              </Link>
            )}
          </div>
        )}
      </header>
      {/* Subnav: inline on wide screens (sits between title and right actions),
          drops to a second row only when it doesn't fit. */}
      {subnav && (
        <div className="flex flex-shrink-0 items-center justify-center border-b border-gray-200 bg-white px-4 py-2 sm:px-8 lg:hidden">
          {subnav}
        </div>
      )}
      {children}
      <FeedbackButton />
    </div>
  );
}
